import React from 'react';
import { Link } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../hooks/useAuth';
import PasswordInput from '../../components/PasswordInput';

const SignupSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .required('Password is required'),
  companyName: Yup.string(),
  agreeToTerms: Yup.boolean().oneOf([true], 'You must agree to the terms'),
});

const Signup: React.FC = () => {
  const { signup } = useAuth();

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-content">Create your account</h2>
        <p className="mt-2 text-sm text-content-muted">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-brand-600 hover:text-brand-500">
            Sign in
          </Link>
        </p>
      </div>

      <Formik
        initialValues={{
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          companyName: '',
          agreeToTerms: false,
        }}
        validationSchema={SignupSchema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            await signup(values);
          } catch (error) {
            // Error handled in auth context
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ errors, touched, isSubmitting }) => (
          <Form className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="label">
                  First name
                </label>
                <Field
                  id="firstName"
                  name="firstName"
                  type="text"
                  className="input"
                  placeholder="John"
                />
                {errors.firstName && touched.firstName && (
                  <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="label">
                  Last name
                </label>
                <Field
                  id="lastName"
                  name="lastName"
                  type="text"
                  className="input"
                  placeholder="Doe"
                />
                {errors.lastName && touched.lastName && (
                  <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="label">
                Email address
              </label>
              <Field
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className="input"
                placeholder="you@company.com"
              />
              {errors.email && touched.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="companyName" className="label">
                Company name (optional)
              </label>
              <Field
                id="companyName"
                name="companyName"
                type="text"
                className="input"
                placeholder="Acme Inc."
              />
            </div>

            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <Field
                id="password"
                name="password"
                as={PasswordInput}
                autoComplete="new-password"
                className="input"
                placeholder="••••••••"
              />
              {errors.password && touched.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
              <p className="mt-1 text-xs text-content-muted">
                Must be at least 8 characters
              </p>
            </div>

            <div className="flex items-start">
              <div className="flex h-5 items-center">
                <Field
                  id="agreeToTerms"
                  name="agreeToTerms"
                  type="checkbox"
                  className="h-4 w-4 rounded border-border-strong text-brand-600 focus:ring-brand-500"
                />
              </div>
              <div className="ml-3 text-sm">
                {/* Real links now that /terms and /privacy exist. They were
                    deliberately plain text while the documents did not — an
                    agreement checkbox pointing at nothing asks someone to
                    consent to something they cannot read. Opens in a new tab so
                    reading them does not discard a part-filled signup form. */}
                <label htmlFor="agreeToTerms" className="font-medium text-content">
                  I agree to the{' '}
                  <Link
                    to="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-brand-600 hover:text-brand-700 underline"
                  >
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link
                    to="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-brand-600 hover:text-brand-700 underline"
                  >
                    Privacy Policy
                  </Link>
                </label>
                {errors.agreeToTerms && touched.agreeToTerms && (
                  <p className="text-red-600">{errors.agreeToTerms}</p>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-primary"
            >
              {isSubmitting ? 'Creating account...' : 'Create account'}
            </button>

            {/* There is no 7-day trial. Signup creates a Free account;
                proposal drafting and the AI actions are granted by hand during
                the private beta. Promising a trial that does not exist is the
                same defect as an agreement checkbox with no agreement. */}
            <p className="text-xs text-content-muted text-center">
              Free account — opportunity search, match scoring and your watch list. No credit
              card. Proposal drafting and AI actions are in private beta.
            </p>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default Signup;
